/**
 * EventTicker — pinned strip showing upcoming market-moving events.
 *
 * MVP keeps things local: a small user-editable list persisted to
 * `localStorage[events.list.v1]`. Each item is { date, label, region }.
 * No external news API yet — that's a Phase F.1 enhancement.
 *
 * Items in the past are auto-hidden; the next 7 days are shown.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { CH_TYPE } from '../../styles/appUi';
import {
  Box, Typography, Chip, IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, TextField, Stack, MenuItem, Select, FormControl, InputLabel,
  Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EventIcon from '@mui/icons-material/Event';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAppTheme } from '../../hooks/useAppTheme';
import api from '../../services/api';

const STORAGE_KEY = 'events.list.v1';
const SERVER_CACHE_KEY = 'events.server.v1';
const SERVER_CACHE_TS_KEY = 'events.server.ts.v1';
const SERVER_REFRESH_INTERVAL_MS = 12 * 60 * 60 * 1000; // 12h

interface MarketEvent {
  id: string;
  date: string; // YYYY-MM-DD
  label: string;
  region: 'IN' | 'US' | 'EU' | 'ASIA' | 'GLOBAL';
  impact: 'low' | 'medium' | 'high';
  /** 'server' = auto-populated from /v1/market-events; 'user' = user added/edited. */
  source?: 'server' | 'user' | 'curated';
}

const load = (): MarketEvent[] => {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (Array.isArray(raw)) return raw;
  } catch { /* ignore */ }
  return [];
};
const persist = (rows: MarketEvent[]) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(rows)); } catch { /* ignore */ }
};
const loadServerCache = (): MarketEvent[] => {
  try {
    const raw = JSON.parse(localStorage.getItem(SERVER_CACHE_KEY) || 'null');
    if (Array.isArray(raw)) return raw;
  } catch { /* ignore */ }
  return [];
};
const persistServerCache = (rows: MarketEvent[]) => {
  try {
    localStorage.setItem(SERVER_CACHE_KEY, JSON.stringify(rows));
    localStorage.setItem(SERVER_CACHE_TS_KEY, String(Date.now()));
  } catch { /* ignore */ }
};

const impactColor = (impact: MarketEvent['impact'], C: any) =>
  impact === 'high' ? { bg: C.bearMuted, fg: C.bear }
  : impact === 'medium' ? { bg: C.warnMuted, fg: C.warn }
  : { bg: C.dimMuted, fg: C.dim };

const daysUntil = (dateStr: string): number | null => {
  if (!dateStr) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr); d.setHours(0, 0, 0, 0);
  if (isNaN(d.getTime())) return null;
  return Math.round((d.getTime() - today.getTime()) / 86_400_000);
};

const EventTicker: React.FC = () => {
  const { C } = useAppTheme();
  const [userEvents, setUserEvents] = useState<MarketEvent[]>(load());
  const [serverEvents, setServerEvents] = useState<MarketEvent[]>(loadServerCache());
  const [editOpen, setEditOpen] = useState(false);
  const [draft, setDraft] = useState<MarketEvent>({ id: '', date: '', label: '', region: 'IN', impact: 'medium', source: 'user' });
  const [syncing, setSyncing] = useState(false);

  useEffect(() => { persist(userEvents); }, [userEvents]);

  // Auto-fetch curated events from the backend on mount + every 12h.
  // Cached server response keeps the ticker populated on cold start even
  // if the server is briefly unreachable.
  const fetchServerEvents = React.useCallback(async () => {
    setSyncing(true);
    try {
      const r = await api.get<{ success: boolean; data: MarketEvent[] }>('/market-events', {
        params: { days: 14 },
        headers: { _suppressToast: 'true' },
      });
      const rows = (r.data?.data || []).map(e => ({ ...e, source: 'server' as const }));
      setServerEvents(rows);
      persistServerCache(rows);
    } catch {
      // Stale cache stays in place — user still sees something
    } finally {
      setSyncing(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const lastTs = (() => { try { return parseInt(localStorage.getItem(SERVER_CACHE_TS_KEY) || '0', 10); } catch { return 0; } })();
    const stale = !lastTs || (Date.now() - lastTs) > SERVER_REFRESH_INTERVAL_MS;
    if (stale) {
      fetchServerEvents();
    }
    const t = setInterval(() => { if (!cancelled) fetchServerEvents(); }, SERVER_REFRESH_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, [fetchServerEvents]);

  // Merge user-added on top of server-curated. User entries with matching id
  // win (overrides or mutes); a user entry with empty label is treated as a
  // "mute" and filters the corresponding server entry out entirely.
  const events = useMemo<MarketEvent[]>(() => {
    const byId = new Map<string, MarketEvent>();
    for (const e of serverEvents) byId.set(e.id, e);
    for (const e of userEvents) byId.set(e.id, e);
    return Array.from(byId.values()).filter(e => e.label.trim().length > 0);
  }, [serverEvents, userEvents]);

  // Show events whose date is within the next 7 days (and today). Past events drop off automatically.
  const upcoming = useMemo(() => {
    return events
      .map(e => ({ e, d: daysUntil(e.date) }))
      .filter(({ d }) => d != null && d >= 0 && d <= 7)
      .sort((a, b) => (a.d || 0) - (b.d || 0));
  }, [events]);

  // User add/update operates on USER events only (server events refresh on their own).
  // Editing a server event creates a user-side override with the same id.
  const addOrUpdate = () => {
    if (!draft.label.trim() || !draft.date) return;
    setUserEvents(prev => {
      const exists = prev.find(e => e.id === draft.id);
      if (exists) {
        return prev.map(e => e.id === draft.id ? { ...draft, source: 'user' } : e);
      }
      const id = draft.id || `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      const next: MarketEvent = { ...draft, id, source: 'user' };
      return [...prev, next];
    });
    setDraft({ id: '', date: '', label: '', region: 'IN', impact: 'medium', source: 'user' });
  };

  // Remove: if it's a USER event, drop it. If it's a SERVER event, mark a
  // hidden user override with empty label so the merge filters it out
  // (server entries can't be deleted, but the user can mute one).
  const remove = (id: string) => {
    setUserEvents(prev => {
      const inUser = prev.some(e => e.id === id);
      if (inUser) return prev.filter(e => e.id !== id);
      // server event → mute by adding a user override with empty label
      return [...prev, { id, date: '', label: '', region: 'GLOBAL', impact: 'low', source: 'user' }];
    });
  };

  return (
    <>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1, mb: 1.25, px: 1.25, py: 0.75,
        borderRadius: 1.5, border: '1px solid', borderColor: 'divider',
        bgcolor: 'background.paper', overflowX: 'auto',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', whiteSpace: 'nowrap' }}>
          <EventIcon sx={{ fontSize: 14 }} />
          <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Events:
          </Typography>
        </Box>
        {upcoming.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            No events in the next 7 days. Click ✎ to add one.
          </Typography>
        ) : (
          upcoming.map(({ e, d }) => {
            const c = impactColor(e.impact, C);
            const when = d === 0 ? 'TODAY' : d === 1 ? 'Tomorrow' : `+${d}d`;
            return (
              <Tooltip
                key={e.id}
                arrow
                title={`${e.label} · ${e.region} · ${e.impact} impact · ${new Date(e.date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}`}
              >
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 0.5,
                  px: 1, py: 0.4, borderRadius: 0.75,
                  bgcolor: c.bg, color: c.fg,
                  border: '1px solid', borderColor: c.fg,
                  whiteSpace: 'nowrap',
                }}>
                  <Typography variant="caption" sx={{ fontWeight: 800, fontSize: CH_TYPE.micro }}>{when}</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700, fontSize: CH_TYPE.small }}>{e.label}</Typography>
                  <Chip label={e.region} size="small" sx={{ height: 18, fontSize: CH_TYPE.micro, fontWeight: 700, bgcolor: 'background.paper' }} />
                </Box>
              </Tooltip>
            );
          })
        )}
        <Box sx={{ flex: 1 }} />
        <Tooltip title={syncing ? 'Syncing…' : 'Re-sync curated events from server'}>
          <span>
            <IconButton size="small" onClick={fetchServerEvents} disabled={syncing}>
              <RefreshIcon fontSize="small" sx={{ ...(syncing ? { animation: 'spin 1s linear infinite', '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } } : {}) }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Edit events">
          <IconButton size="small" onClick={() => setEditOpen(true)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Market events</DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Events in the next 7 days appear on the Dashboard ticker. The curated set (FOMC / RBI / NFP /
            CPI / Budget) is auto-populated from the server and refreshed every 12&nbsp;hours; you can add
            your own events below or delete any (server-curated entries get a "mute" override).
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.5 }}>
            <TextField
              type="date"
              label="Date"
              size="small"
              value={draft.date}
              onChange={(e) => setDraft(d => ({ ...d, date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 150 }}
            />
            <TextField
              label="Event"
              size="small"
              value={draft.label}
              onChange={(e) => setDraft(d => ({ ...d, label: e.target.value }))}
              sx={{ flex: 1 }}
            />
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Region</InputLabel>
              <Select
                value={draft.region}
                label="Region"
                onChange={(e) => setDraft(d => ({ ...d, region: e.target.value as MarketEvent['region'] }))}
              >
                {(['IN', 'US', 'EU', 'ASIA', 'GLOBAL'] as const).map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel>Impact</InputLabel>
              <Select
                value={draft.impact}
                label="Impact"
                onChange={(e) => setDraft(d => ({ ...d, impact: e.target.value as MarketEvent['impact'] }))}
              >
                {(['low', 'medium', 'high'] as const).map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </Select>
            </FormControl>
            <Button onClick={addOrUpdate} variant="contained" size="small" disabled={!draft.label.trim() || !draft.date}>
              {draft.id ? 'Update' : 'Add'}
            </Button>
          </Stack>
          {events.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No events yet.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Event</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Region</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Impact</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {events.map(e => {
                  const isServer = (e.source === 'server' || e.source === 'curated');
                  return (
                    <TableRow key={e.id} hover>
                      <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{e.date || '—'}</TableCell>
                      <TableCell>
                        {e.label}
                        {isServer && (
                          <Chip
                            label="auto"
                            size="small"
                            sx={{ ml: 1, height: 18, fontSize: CH_TYPE.micro, bgcolor: C.neutralMuted, color: C.neutral, fontWeight: 700 }}
                          />
                        )}
                      </TableCell>
                      <TableCell>{e.region}</TableCell>
                      <TableCell>{e.impact}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <IconButton size="small" onClick={() => setDraft({ ...e, source: 'user' })}><EditIcon fontSize="small" /></IconButton>
                        <Tooltip arrow title={isServer ? 'Mute this auto event' : 'Delete'}>
                          <IconButton size="small" onClick={() => remove(e.id)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EventTicker;
